// src/pages/ContactPage.jsx
import React, { useState } from 'react';
import {
  FaLinkedin,
  FaInstagram,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { sendContactEmail } from '../services/emailService';
import { useTheme } from '../context/ThemeContext';
import contactHeroBg from '../assets/images/services/tech-guy.jpg';

const ContactPage = () => {
  const { isDark } = useTheme();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.message
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await sendContactEmail(formData);

      setIsSubmitted(true);

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });

      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      console.error(err);
      setError(
        err.message || 'Failed to send message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: FaMapMarkerAlt,
      title: 'Office Location',
      details: ['Fort McMurray, Alberta, Canada']
    },
    {
      icon: FaEnvelope,
      title: 'Email Us',
      details: ['info@dorots.com', 'support@dorots.com'],
      action: 'mailto:info@dorots.com'
    },
    {
      icon: FaPhone,
      title: 'Call Us',
      details: ['+1 (780) 799-5585'],
      action: 'tel:+17807995585'
    },
    {
      icon: FaClock,
      title: 'Business Hours',
      details: ['Monday - Friday: 9AM - 6PM', 'Saturday - Sunday: Closed']
    }
  ];

  const services = [
    'Select a service',
    'Custom Software Development',
    'Management System Consulting',
    'Ecommerce Applications',
    'Software Licensing',
    'WorkEikon - EHS Software',
    'Quinthex - LMS Software',
    'Schematz - ERP Solution',
    'Fundtz - Financial Software',
    'Other Inquiry'
  ];

  const socialLinks = [
    {
      href: 'https://www.linkedin.com/company/dorots-ltd/posts/?feedView=all&viewAsMember=true',
      icon: FaLinkedin,
      label: 'LinkedIn'
    },
    {
      href: 'https://www.instagram.com/dorotsltd/',
      icon: FaInstagram,
      label: 'Instagram'
    },
    {
      href: 'https://www.x.com/DorotsL28336/',
      icon: FaXTwitter,
      label: 'Twitter/X'
    }
  ];

  return (
    <div className="pt-20">

      {/* Hero Section */}
      <section
        className={`relative py-16 sm:py-20 lg:py-24 overflow-hidden ${
          isDark ? 'bg-black' : 'bg-gray-50'
        }`}
      >
        <div className="absolute inset-0 z-0">
          <img
            src={contactHeroBg}
            alt="Contact DOROTS"
            className="w-full h-full object-cover object-center"
          />

          <div
            className={`absolute inset-0 ${
              isDark
                ? 'bg-gradient-to-r from-black/85 via-black/70 to-black/50'
                : 'bg-gradient-to-r from-black/65 via-black/50 to-black/30'
            }`}
          />
        </div>

        {/* Blue glow effects */}
        {isDark && (
          <>
            <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-blue-500/15 rounded-full blur-3xl z-0" />
            <div className="absolute bottom-[-250px] right-[-150px] w-[600px] h-[600px] bg-blue-600/15 rounded-full blur-3xl z-0" />
          </>
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">

            <div className="mb-4">
              <span
                className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block backdrop-blur-sm ${
                  isDark
                    ? 'text-blue-400 bg-blue-500/20 border border-blue-500/30'
                    : 'text-blue-600 bg-blue-100 border border-blue-200'
                }`}
              >
                GET IN TOUCH
              </span>
            </div>

            <h1
              className={`text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight ${
                isDark ? 'text-white' : 'text-white'
              }`}
            >
              Let's{' '}
              <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 bg-clip-text text-transparent">
                Connect
              </span>
            </h1>

            <p
              className={`text-lg sm:text-xl leading-relaxed ${
                isDark ? 'text-gray-200' : 'text-white'
              }`}
            >
              Have a project in mind? Looking for a custom solution? We'd love
              to hear from you. Reach out and let's discuss how we can help
              transform your business.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className={`py-16 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl text-center transition-all duration-300 hover:transform hover:scale-[1.02] ${
                  isDark
                    ? 'bg-white/5 border border-white/10 hover:border-blue-500/30 hover:bg-white/10'
                    : 'bg-gray-50 border border-gray-200 hover:border-blue-400 hover:shadow-lg'
                }`}
              >
                <info.icon
                  className={`text-4xl mb-4 mx-auto ${
                    isDark ? 'text-blue-600' : 'text-blue-600'
                  }`}
                />

                <h3
                  className={`text-lg font-semibold mb-3 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {info.title}
                </h3>

                {info.details.map((detail, idx) =>
                  info.action ? (
                    <a
                      key={idx}
                      href={info.action}
                      className={`block text-sm ${
                        isDark
                          ? 'text-gray-400 hover:text-blue-400'
                          : 'text-gray-600 hover:text-blue-600'
                      } transition-colors`}
                    >
                      {detail}
                    </a>
                  ) : (
                    <p
                      key={idx}
                      className={`text-sm ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      {detail}
                    </p>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section
        className={`py-16 ${
          isDark ? 'bg-gray-900/30' : 'bg-blue-50'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Contact Form */}
            <div
              className={`rounded-2xl p-6 sm:p-8 ${
                isDark
                  ? 'bg-white/5 border border-white/10'
                  : 'bg-white border border-gray-200 shadow-lg'
              }`}
            >
              <div className="mb-6">
                <h2
                  className={`text-2xl sm:text-3xl font-bold mb-2 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Send us a message
                </h2>

                <p
                  className={`text-sm ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Fill out the form below and we'll get back to you within 24
                  hours
                </p>
              </div>

              {isSubmitted ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
                  <div className="text-5xl mb-3">✅</div>

                  <h3
                    className={`text-xl font-semibold mb-2 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Message Sent Successfully!
                  </h3>

                  <p
                    className={
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }
                  >
                    Thank you for reaching out. We'll get back to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">

                  {/* Error Alert */}
                  {error && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-500 flex items-start gap-3">
                      <span className="text-lg mt-0.5">⚠️</span>

                      <div>
                        <p className="font-semibold text-sm">Error</p>
                        <p className="text-sm opacity-90">{error}</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setError('')}
                        className="ml-auto text-red-500 hover:text-red-600 transition-colors"
                        aria-label="Close error"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* First & Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        First Name *
                      </label>

                      <input
                        type="text"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          isDark
                            ? 'bg-white/10 border-white/20 text-white focus:border-blue-500'
                            : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        Last Name *
                      </label>

                      <input
                        type="text"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          isDark
                            ? 'bg-white/10 border-white/20 text-white focus:border-blue-500'
                            : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'
                        }`}
                      />
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        Email Address *
                      </label>

                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          isDark
                            ? 'bg-white/10 border-white/20 text-white focus:border-blue-500'
                            : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        Phone Number
                      </label>

                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          isDark
                            ? 'bg-white/10 border-white/20 text-white focus:border-blue-500'
                            : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'
                        }`}
                      />
                    </div>
                  </div>

                  {/* Company & Service */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        Company Name
                      </label>

                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          isDark
                            ? 'bg-white/10 border-white/20 text-white focus:border-blue-500'
                            : 'bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500'
                        }`}
                      />
                    </div>

                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${
                          isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        Service Interested In *
                      </label>

                      <select
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          isDark
                            ? 'bg-white/10 border-white/20 text-white'
                            : 'bg-gray-50 border-gray-300 text-gray-900'
                        }`}
                      >
                        {services.map((service, idx) => (
                          <option
                            key={idx}
                            value={service}
                            className={
                              isDark ? 'bg-gray-900' : 'bg-white'
                            }
                          >
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Message *
                    </label>

                    <textarea
                      name="message"
                      rows="5"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or inquiry..."
                      className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none ${
                        isDark
                          ? 'bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500'
                          : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-500'
                      }`}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`
                      w-full py-3 rounded-xl font-semibold
                      transition-all duration-300
                      bg-gradient-to-r from-blue-500 to-blue-700
                      text-white
                      ${
                        isSubmitting
                          ? 'opacity-80 cursor-not-allowed'
                          : 'hover:scale-[1.02] hover:shadow-lg'
                      }
                    `}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />

                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>

                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </button>

                  <p
                    className={`text-xs text-center ${
                      isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}
                  >
                    By submitting this form, you agree to our privacy policy
                    and consent to be contacted by DOROTS LTD.
                  </p>
                </form>
              )}
            </div>

            {/* Map & Additional Info */}
            <div className="space-y-6">

              {/* Map */}
              <div
                className={`rounded-2xl overflow-hidden ${
                  isDark
                    ? 'border border-white/10'
                    : 'border border-gray-200 shadow-lg'
                }`}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d22150.694654334214!2d-111.386499!3d56.726299!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5383e5c5a5b5b5b5%3A0x5b5b5b5b5b5b5b5b!2sFort%20McMurray%2C%20AB%2C%20Canada!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="DOROTS Office Location"
                />
              </div>

              {/* FAQ */}
              <div
                className={`rounded-2xl p-6 ${
                  isDark
                    ? 'bg-white/5 border border-white/10'
                    : 'bg-white border border-gray-200 shadow-lg'
                }`}
              >
                <h3
                  className={`text-xl font-bold mb-4 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Frequently Asked Questions
                </h3>

                <div className="space-y-4">

                  <div>
                    <h4
                      className={`font-semibold mb-1 ${
                        isDark ? 'text-blue-400' : 'text-blue-600'
                      }`}
                    >
                      How quickly will I get a response?
                    </h4>

                    <p
                      className={`text-sm ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      We typically respond within 24 business hours.
                    </p>
                  </div>

                  <div>
                    <h4
                      className={`font-semibold mb-1 ${
                        isDark ? 'text-blue-400' : 'text-blue-600'
                      }`}
                    >
                      Do you offer custom solutions?
                    </h4>

                    <p
                      className={`text-sm ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      Yes! We specialize in custom software development
                      tailored to your needs.
                    </p>
                  </div>

                  <div>
                    <h4
                      className={`font-semibold mb-1 ${
                        isDark ? 'text-blue-400' : 'text-blue-600'
                      }`}
                    >
                      Do you provide support after deployment?
                    </h4>

                    <p
                      className={`text-sm ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      Absolutely. We offer comprehensive post-implementation
                      support.
                    </p>
                  </div>

                </div>
              </div>

              {/* Social Links */}
              <div
                className={`rounded-2xl p-6 ${
                  isDark
                    ? 'bg-white/5 border border-white/10'
                    : 'bg-white border border-gray-200 shadow-lg'
                }`}
              >
                <h3
                  className={`text-xl font-bold mb-4 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Connect With Us
                </h3>

                <div className="flex gap-4">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 ${
                        isDark
                          ? 'bg-white/10 hover:bg-blue-500/20 text-white'
                          : 'bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-600'
                      }`}
                      aria-label={social.label}
                    >
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className={`py-16 ${isDark ? 'bg-black' : 'bg-white'}`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h2
            className={`text-2xl sm:text-3xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Ready to Transform Your Business?
          </h2>

          <p
            className={`mb-8 ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            Let's discuss how DOROTS can help you achieve your goals
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">

            <a
              href="tel:+17807995585"
              className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Call Us Now
            </a>

            <a
              href="mailto:info@dorots.com"
              className={`px-8 py-3 rounded-xl border font-semibold transition-all duration-300 ${
                isDark
                  ? 'border-white/20 text-white hover:bg-white/10'
                  : 'border-blue-300 text-blue-700 hover:bg-blue-100'
              }`}
            >
              Email Our Team
            </a>

          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;